import type React from 'react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Collaborator } from '@/app/models/collaborator';
import { ExtraCollaboratorResponse } from '@/app/models/extraCollaborator';
import { useCreateCollaboratorExtraMutation } from '@/app/services/extraCollaboratorApi';
import { useSearchPersonQuery } from '@/app/services/personApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import ModalLabel from '../common/ModalLabel';

interface NewEditExtraCollaboratorProps {
  collaboratorSelect: ExtraCollaboratorResponse | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditExtraCollaborator(props: NewEditExtraCollaboratorProps) {
  const theme = useTheme();
  const { collaboratorSelect, setDrawerOpen } = props;
  const { t } = useTranslation(['extraCollaborator', 'common']);

  const [selectedPriority, setSelectedPriority] = useState<number | null>(collaboratorSelect?.priority || null);

  const [collaborator, setCollaborator] = useState<Collaborator | null>(collaboratorSelect?.person || null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaborators, isFetching: isLoadingCollaborators } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const [createExtraCollaborator] = useCreateCollaboratorExtraMutation();

  const handleSubmit = async () => {
    let response = {};

    if (!selectedPriority || !collaborator) return;

    setDrawerOpen(false);
    toast.info(
      t('common:processing.wait.a.moment', {
        position: toast.POSITION.TOP_RIGHT,
      }),
    );
    response = await createExtraCollaborator({
      badge: collaborator?.badge as string,
      priority: selectedPriority,
    });
    if ('data' in response) {
      toast.dismiss();
      toast.success(
        t('historic.collaborator.priority.set.success', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    } else {
      toast.error(
        t('common:processing.failed', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle
          title={t('Colaborador Extra')}
          subtitle={t('historic.extra.collaborator.subtitle.modal') as string}
        />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 4,
            gap: theme.spacing(2),
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 4 }}>
            <ModalLabel
              title={`${t('common:collaborator')}`}
              subtitle={t('historic.input.collaborator.subtitle') as string}
            />

            <Box sx={{ display: 'flex', width: '100%' }}>
              {!collaboratorSelect ? (
                <GenericAutoComplete
                  value={collaborator}
                  onChange={(value) => {
                    setCollaborator(value);
                  }}
                  equalityTest={(option, value) => option?.email === value?.email}
                  getOptionLabel={(option) => `${option?.badge} - ${option?.name}`}
                  onSearch={(value) => {
                    setCollaboratorSearch(value);
                  }}
                  options={collaboratorSearch !== '' && collaborators?.content ? collaborators?.content : []}
                  placeholder={t('historic.input.search.collaborator') || ''}
                  loading={isLoadingCollaborators}
                  sx={{ width: theme.spacing(50) }}
                />
              ) : (
                <TextField
                  value={`${collaborator?.badge} - ${collaborator?.name}`}
                  variant="outlined"
                  size="small"
                  disabled
                  sx={{ width: '100%' }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'block', minWidth: theme.spacing(31) }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('common:priority')}
            </Typography>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <TextField
                id="outlined-number"
                type="number"
                size="small"
                variant="outlined"
                required
                inputProps={{ min: 1, max: 100 }}
                value={selectedPriority}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.value) {
                    setSelectedPriority(Number(event.target.value));
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons
          onSave={handleSubmit}
          onCancel={handleCancel}
          disabled={!selectedPriority || selectedPriority === 0 || !collaborator}
        />
      </Box>
    </Box>
  );
}
